import { Club } from '@/types';
import { SportBadge } from './SportBadge';
import { RankBadge } from './RankBadge';
import { FollowClubButton } from './FollowButton';
import { MapPin, Users, Star, UserPlus, Swords } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useNavigate } from 'react-router-dom';
import { formatDistance } from '@/lib/geo-utils';
import type { Database } from '@/integrations/supabase/types';

type AppRank = Database['public']['Enums']['app_rank'];

interface ClubCardProps {
  club: Club;
  onClick?: () => void;
  className?: string;
  showFollowButton?: boolean;
  distance?: number; // km
}

export function ClubCard({
  club,
  onClick,
  className,
  showFollowButton = true,
  distance,
}: ClubCardProps) {
  const navigate = useNavigate();
  
  const handleClick = () => {
    if (onClick) {
      onClick();
    } else {
      navigate(`/clubs/${club.id}`);
    }
  };
  
  return (
    <div 
      onClick={handleClick} 
      className={cn(
        'match-tile cursor-pointer group',
        'active:scale-[0.98]',
        className
      )}
    >
      {/* Header Row - Name Left Aligned */}
      <div className="flex items-center justify-start gap-2 mb-2">
        <h3 className="text-sm font-bold text-foreground truncate">{club.name}</h3>
        {club.rank && <RankBadge rank={club.rank as AppRank} size="sm" />}
      </div>

      {/* Sub Header - Avatar & Info */}
      <div className="flex items-start gap-3 mb-2">
        {/* Avatar */}
        <div className="w-12 h-12 rounded-lg overflow-hidden ring-2 ring-accent/30 group-hover:ring-accent/60 transition-all shrink-0">
          <img src={club.avatar} alt={club.name} className="w-full h-full object-cover" />
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0 space-y-1.5">
          <div className="flex items-center gap-1.5 flex-wrap">
            <SportBadge sport={club.sport} size="sm" />
            <div className="flex items-center gap-0.5 text-xs">
              <Users className="w-3.5 h-3.5 text-accent" />
              <span className="text-foreground font-semibold">{club.memberCount}</span>
            </div>
            <div className="flex items-center gap-0.5 text-xs">
              <Star className="w-3.5 h-3.5 text-rank-gold fill-rank-gold" />
              <span className="text-foreground font-semibold">{club.rating.toFixed(1)}</span>
            </div>
          </div>

          {(club.location || distance !== undefined) && (
            <div className="flex items-center gap-1.5 text-xs">
              <MapPin className="w-3.5 h-3.5 text-accent shrink-0" />
              {distance !== undefined && (
                <span className="text-primary font-semibold shrink-0">{formatDistance(distance)}</span>
              )}
              {club.location && (
                <span className="text-muted-foreground truncate">{club.location}</span>
              )}
            </div>
          )}
        </div>

        {/* Follow Button */}
        {showFollowButton && (
          <div className="shrink-0">
            <FollowClubButton clubId={club.id} size="sm" showLabel={false} />
          </div>
        )}
      </div>

      {/* Footer - Tags & Status */}
      <div className="flex items-center justify-between pt-2 border-t border-accent/10">
        <div className="flex items-center gap-1.5 flex-wrap">
          {club.isRecruiting && (
            <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-sport-tennis/20 text-sport-tennis border border-sport-tennis/30 flex items-center gap-0.5">
              <UserPlus className="w-3 h-3" />
              Tuyển
            </span>
          )}
          {club.isLookingForMatch && (
            <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-accent/20 text-accent border border-accent/30 flex items-center gap-0.5">
              <Swords className="w-3 h-3" />
              Giao lưu
            </span>
          )}
          {club.tags.slice(0, 2).map(tag => (
            <span key={tag} className="text-[10px] px-1.5 py-0.5 rounded text-muted-foreground bg-secondary border border-border/50">
              {tag}
            </span>
          ))}
        </div>
        {club.tags.length > 2 && (
          <span className="text-[10px] text-muted-foreground">
            +{club.tags.length - 2}
          </span>
        )}
      </div>
    </div>
  );
}